import Card from "@/components/Card";
import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import ImageControl from "@/components/ImageControll";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import SkeletonCard from "@/components/SkeletonCard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { formatDateIndonesia, formatRupiah, getInitials } from "@/lib/utils";
import {
  getListJumlahTanggungan,
  getListPekerjaan,
  getListDesil,
} from "@/services/getData";
import {
  DataKeluarga,
  DataPendaftaran,
  DataSiswa,
  JumlahTanggungan,
  Pekerjaan,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Loader2,
  Users,
  XCircle,
} from "lucide-react";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { GiBackwardTime } from "react-icons/gi";
import { MdModeEdit } from "react-icons/md";

// --- Types ---
type ValidationState = {
  id_data_keluarga?: string;
  id_ref_pekerjaan: string;
  id_ref_pekerjaan_old: string;
  is_valid?: boolean;
  id_ref_penghasilan: string;
  id_ref_penghasilan_old: string;
  penghasilan: number;
  penghasilan_old: number;
  nama_hubungan_keluarga?: string;
  catatan_verifikasi?: string;
  id_ref_desil?: string;
  nik?: string;
  isDesilRequired?: boolean;
};

type ValidationTanggunganState = {
  id_data_pendaftaran: string;
  jumlah_tanggungan_orangtua_wali: string;
  jumlah_tanggungan_orangtua_wali_old: string;
  is_valid?: boolean;
  biaya_hidup_keluarga_perbulan?: string | number | null;
  catatan_verifikasi_tanggungan?: string;
  is_ada_sarjana_keluarga?: string | boolean | null;
  nama_sarjana_keluarga?: string | null;
};

type ValidationPantiAsuhanState = {
  id_data_pendaftaran: string;
  catatan_verifikator: string;
  is_valid?: boolean;
};

// --- API Calls ---
const patchDataValidate = async (form: ValidationState) => {
  const response = await api.patch(
    `/verifikator/peserta/data-keluarga/validate`,
    form,
  );
  return response.data;
};

const patchDataValidateJumlahTanggungan = async (
  form: ValidationTanggunganState,
) => {
  const response = await api.patch(
    `/verifikator/peserta/data-keluarga/validate-jumlah-tanggungan`,
    form,
  );
  return response.data;
};

const patchDataValidatePantiAsuhan = async (
  form: ValidationPantiAsuhanState,
) => {
  const response = await api.patch(
    `/verifikator/peserta/data-keluarga/validate-berkas-panti-asuhan`,
    form,
  );
  return response.data;
};

const patchDataReset = async ({
  id_data_keluarga,
}: {
  id_data_keluarga: string;
}) => {
  const response = await api.patch(`/verifikator/peserta/data-keluarga/reset`, {
    id_data_keluarga,
  });
  return response.data;
};

const patchDataResetValidationJumlahTanggungan = async ({
  id_data_pendaftaran,
}: {
  id_data_pendaftaran: string;
}) => {
  const response = await api.patch(
    `/verifikator/peserta/data-keluarga/reset-validation-jumlah-tanggungan`,
    { id_data_pendaftaran },
  );
  return response.data;
};

const patchDataResetValidationPantiAsuhan = async ({
  id_data_pendaftaran,
}: {
  id_data_pendaftaran: string;
}) => {
  const response = await api.patch(
    `/verifikator/peserta/data-keluarga/reset-validation-berkas-panti-asuhan`,
    { id_data_pendaftaran },
  );
  return response.data;
};

// --- Helper Component: DataRow ---
// Mengganti elemen <table> yang kaku menjadi baris list yang modern dan responsif
const DataRow = ({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}) => (
  <div className="flex flex-col sm:flex-row sm:items-start py-3 border-b border-slate-100 dark:border-slate-800/60 last:border-0">
    <span className="text-sm font-medium text-slate-500 dark:text-slate-400 sm:w-1/3 shrink-0">
      {label}
    </span>
    <span
      className={`text-sm mt-1 sm:mt-0 ${highlight ? "font-bold text-primary" : "font-medium text-slate-900 dark:text-slate-100"}`}
    >
      {value || "-"}
    </span>
  </div>
);

export default function Keluarga({
  dataPendaftaran,
  dataKeluarga,
  dataSiswa,
  isPending,
}: {
  dataPendaftaran: DataPendaftaran | undefined;
  dataKeluarga: DataKeluarga[] | undefined;
  dataSiswa: DataSiswa | undefined;
  isPending: boolean;
}) {
  const { user } = useUser();
  const isVerifikatorUktAll = user?.jenis_user.includes("verifikator_ukt_all");

  const [showInput, setShowInput] = useState(false);
  const [showInputValid, setShowInputValid] = useState(false);
  const [showInputJumlahTanggungan, setShowInputJumlahTanggungan] =
    useState(false);
  const [showInputPantiAsuhan, setShowInputPantiAsuhan] = useState(false);

  const [openPekerjaan, setOpenPekerjaan] = useState(false);
  const [openDesil, setOpenDesil] = useState(false);
  const [openJumlahTanggungan, setOpenJumlahTanggungan] = useState(false);
  const [openSarjana, setOpenSarjana] = useState(false);
  const [selectedIdDataKeluarga, setSelectedIdDataKeluarga] = useState("");

  const [form, setForm] = useState<ValidationState>({
    id_ref_pekerjaan: "",
    id_ref_pekerjaan_old: "",
    id_ref_penghasilan: "",
    id_ref_penghasilan_old: "",
    penghasilan: 0,
    penghasilan_old: 0,
    catatan_verifikasi: "",
    id_ref_desil: "",
    nik: "",
    isDesilRequired: true,
  });

  const [formTanggungan, setFormTanggungan] =
    useState<ValidationTanggunganState>({
      id_data_pendaftaran: String(dataPendaftaran?.id_data_pendaftaran) || "",
      jumlah_tanggungan_orangtua_wali: "",
      jumlah_tanggungan_orangtua_wali_old: "",
      is_valid: false,
      biaya_hidup_keluarga_perbulan: "",
      catatan_verifikasi_tanggungan: "",
      is_ada_sarjana_keluarga:
        String(dataPendaftaran?.is_ada_sarjana_keluarga) == "1"
          ? "1"
          : String(dataPendaftaran?.is_ada_sarjana_keluarga) == "0"
            ? "0"
            : "",
      nama_sarjana_keluarga: dataPendaftaran?.nama_sarjana_keluarga || "",
    });

  useEffect(() => {
    if (dataPendaftaran) {
      setFormTanggungan((prev) => ({
        ...prev,
        is_ada_sarjana_keluarga:
          String(dataPendaftaran.is_ada_sarjana_keluarga) === "1"
            ? "1"
            : String(dataPendaftaran.is_ada_sarjana_keluarga) === "0"
              ? "0"
              : prev.is_ada_sarjana_keluarga,
        nama_sarjana_keluarga:
          dataPendaftaran.nama_sarjana_keluarga || prev.nama_sarjana_keluarga,
      }));
    }
  }, [
    dataPendaftaran?.is_ada_sarjana_keluarga,
    dataPendaftaran?.nama_sarjana_keluarga,
  ]);

  const [formPantiAsuhan, setFormPantiAsuhan] =
    useState<ValidationPantiAsuhanState>({
      id_data_pendaftaran: String(dataPendaftaran?.id_data_pendaftaran) || "",
      is_valid: false,
      catatan_verifikator: "",
    });

  const { data: dataPekerjaan } = useQuery({
    queryKey: ["listPekerjaan"],
    queryFn: getListPekerjaan,
    select: (data) =>
      data?.map((pekerjaan: Pekerjaan) => ({
        label: pekerjaan.pekerjaan,
        value: `${pekerjaan.id_ref_pekerjaan}`,
      })),
  });

  const { data: dataJumlahTanggungan } = useQuery({
    queryKey: ["listJumlahTanggungan"],
    queryFn: getListJumlahTanggungan,
    select: (data) =>
      data?.map((dataJumlahTanggungan: JumlahTanggungan) => ({
        label: `${dataJumlahTanggungan.jumlah_tanggungan_text}`,
        value: `${dataJumlahTanggungan.id_ref_jumlah_tanggungan}`,
      })),
  });

  const { data: dataDesil } = useQuery({
    queryKey: ["listDesil"],
    queryFn: getListDesil,
    select: (data) =>
      data?.map((desil: any) => ({
        label: desil.desil,
        value: `${desil.id_ref_desil}`,
      })),
  });

  const queryClient = useQueryClient();

  const {
    mutate,
    isPending: isPendingValidate,
    error,
  } = useMutation({
    mutationFn: patchDataValidate,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["peserta"] });
      setShowInput(false);
      setShowInputValid(false);
      setForm((prev) => ({
        ...prev,
        id_ref_pekerjaan: "",
        id_ref_pekerjaan_old: "",
        id_ref_penghasilan: "",
        id_ref_penghasilan_old: "",
        penghasilan: 0,
        penghasilan_old: 0,
        catatan_verifikasi: "",
        id_ref_desil: "",
        nik: "",
        isDesilRequired: true,
      }));
      toast.success(data?.message || "Success.");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const {
    mutate: mutateValidateJumlahTanggungan,
    isPending: isPendingValidateJumlahTanggungan,
    error: errorValidateJumlahTanggungan,
  } = useMutation({
    mutationFn: patchDataValidateJumlahTanggungan,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["peserta"] });
      setShowInputJumlahTanggungan(false);
      toast.success(data?.message || "Success.");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const {
    mutate: mutateValidatePantiAsuhan,
    isPending: isPendingValidatePantiAsuhan,
    error: errorValidatePantiAsuhan,
  } = useMutation({
    mutationFn: patchDataValidatePantiAsuhan,
    onSuccess: (data) => {
      setShowInputPantiAsuhan(false);
      queryClient.invalidateQueries({ queryKey: ["peserta"] });
      toast.success(data?.message || "Success.");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const { mutate: mutateResetValidation, isPending: isPendingResetValidation } =
    useMutation({
      mutationFn: patchDataReset,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["peserta"] });
        toast.success(data?.message || "Success.");
        setForm((prev) => ({
          ...prev,
          id_ref_pekerjaan: "",
          id_ref_pekerjaan_old: "",
          id_ref_penghasilan: "",
          id_ref_penghasilan_old: "",
          penghasilan: 0,
          penghasilan_old: 0,
          id_ref_desil: "",
          nik: "",
          isDesilRequired: true,
        }));
      },
      onError: (error: any) =>
        toast.error(
          error?.response?.data?.message || "An unexpected error occurred.",
        ),
    });

  const {
    mutate: mutateResetValidationJumlahTanggungan,
    isPending: isPendingResetValidationJumlahTanggungan,
  } = useMutation({
    mutationFn: patchDataResetValidationJumlahTanggungan,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["peserta"] });
      toast.success(data?.message || "Success.");
    },
    onError: (error: any) =>
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred.",
      ),
  });

  const {
    mutate: mutateResetValidationPantiAsuhan,
    isPending: isPendingResetValidationPantiAsuhan,
  } = useMutation({
    mutationFn: patchDataResetValidationPantiAsuhan,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["peserta"] });
      toast.success(data?.message || "Success.");
    },
    onError: (error: any) =>
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred.",
      ),
  });

  // Handlers
  const handleValidate = (id_data_keluarga: string, is_valid: boolean) =>
    mutate({ ...form, id_data_keluarga, is_valid });
  const handleValidateJumlahTanggungan = (
    id_data_pendaftaran: string,
    is_valid: boolean,
  ) =>
    mutateValidateJumlahTanggungan({
      ...formTanggungan,
      is_valid,
      id_data_pendaftaran,
    });
  const handleValidatePantiAsuhan = (
    id_data_pendaftaran: string,
    is_valid: boolean,
  ) =>
    mutateValidatePantiAsuhan({
      ...formPantiAsuhan,
      is_valid,
      id_data_pendaftaran,
    });

  const handleReset = (id_data_keluarga: string) =>
    mutateResetValidation({ id_data_keluarga });
  const handleResetValidationJumlahTanggungan = (id_data_pendaftaran: string) =>
    mutateResetValidationJumlahTanggungan({ id_data_pendaftaran });
  const handleResetValidationPantiAsuhan = (id_data_pendaftaran: string) =>
    mutateResetValidationPantiAsuhan({ id_data_pendaftaran });

  const berkasKartuKeluarga = dataPendaftaran?.data_berkas.find(
    (berkas) =>
      berkas.sys_berkas?.jenis_berkas?.slug_jenis_berkas === "Kartu_Keluarga",
  );

  if (isPending)
    return (
      <div className="grid xl:grid-cols-2 grid-cols-1 gap-6">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );

  return (
    <div className="flex flex-col gap-8 w-full">
      {/* INFO ALERT */}
      <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900/50">
        <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-500" />
        <AlertTitle className="text-blue-800 dark:text-blue-400 font-bold ml-2">
          Data Keluarga Wajib Validasi
        </AlertTitle>
        <AlertDescription className="text-blue-700/80 dark:text-blue-500/80 ml-2 mt-2">
          <ul className="list-disc list-inside space-y-1 font-medium">
            <li>
              Pekerjaan & Pendapatan (Cocokkan dengan Slip Gaji/Ket. Desa)
            </li>
            <li>Jumlah Tanggungan (Cocokkan dengan Kartu Keluarga)</li>
            <li>Berkas Panti Asuhan Siswa (Jika ada)</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* --- DATA KELUARGA (AYAH/IBU/WALI) --- */}
      {dataKeluarga && dataKeluarga.length > 0 ? (
        dataKeluarga.map((item) => {
          const isIbu =
            String(item.id_ref_hubungan_keluarga) === "2" ||
            String(item.hubungan_keluarga?.id_ref_hubungan_keluarga) === "2" ||
            item.hubungan_keluarga?.hubungan_keluarga?.toLowerCase() === "ibu";
          const isAyah =
            String(item.id_ref_hubungan_keluarga) === "1" ||
            String(item.hubungan_keluarga?.id_ref_hubungan_keluarga) === "1" ||
            item.hubungan_keluarga?.hubungan_keluarga?.toLowerCase() === "ayah";
          const isAyahMeninggal = dataKeluarga.some(
            (dk) =>
              (String(dk.id_ref_hubungan_keluarga) === "1" ||
                String(dk.hubungan_keluarga?.id_ref_hubungan_keluarga) ===
                  "1" ||
                dk.hubungan_keluarga?.hubungan_keluarga?.toLowerCase() ===
                  "ayah") &&
              dk.status === "M",
          );
          const isDesilRequired = isIbu
            ? isAyahMeninggal
            : isAyah
              ? !isAyahMeninggal
              : true;

          const extension = item.path_slip_gaji
            ?.split(".")
            .pop()
            ?.toLowerCase();
          const isValid = item.is_valid;
          const isPendingItemAction =
            isPendingResetValidation || isPendingValidate;

          return (
            <Card
              key={item.id_data_keluarga}
              className="overflow-hidden border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl"
            >
              <div className="grid xl:grid-cols-2 grid-cols-1 divide-y xl:divide-y-0 xl:divide-x divide-slate-100 dark:divide-slate-800">
                {/* KIRI: DOKUMEN SLIP GAJI */}
                <div className="flex flex-col bg-slate-50/50 dark:bg-slate-900/20">
                  <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
                    <div className="p-2 bg-primary/10 text-primary rounded-lg">
                      <FileText size={20} />
                    </div>
                    <div>
                      <h2 className="font-bold text-slate-800 dark:text-slate-200 text-base">
                        Dokumen Penghasilan
                      </h2>
                      <p className="text-xs text-slate-500">
                        Slip Gaji / S.Ket Kepala Desa
                      </p>
                    </div>
                  </div>
                  <div className="relative h-[60vh] xl:h-[70vh] w-full bg-slate-200/50 dark:bg-slate-950 flex items-center justify-center p-2">
                    {extension === "pdf" ? (
                      <iframe
                        src={item?.path_slip_gaji}
                        title="Slip Gaji"
                        className="absolute inset-0 h-full w-full rounded-b-xl xl:rounded-bl-xl xl:rounded-br-none shadow-inner bg-white"
                      />
                    ) : item.path_slip_gaji ? (
                      <ImageControl imageUrl={item.path_slip_gaji} />
                    ) : (
                      <div className="text-slate-400 flex flex-col items-center">
                        <FileText size={40} className="mb-2 opacity-50" />
                        <p className="text-sm font-medium">
                          Dokumen tidak tersedia
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* KANAN: DATA & AKSI */}
                <div className="flex flex-col bg-white dark:bg-slate-900">
                  {/* Card Header Kanan */}
                  <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/20">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
                        <AvatarFallback className="bg-primary/10 text-primary font-bold text-sm uppercase">
                          {getInitials(
                            item.hubungan_keluarga?.hubungan_keluarga || "?",
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h2 className="font-bold text-lg text-slate-800 dark:text-slate-100 uppercase">
                          Data {item.hubungan_keluarga?.hubungan_keluarga}
                        </h2>
                        {isValid === true && (
                          <Badge variant="success" className="mt-1 shadow-none">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Valid
                          </Badge>
                        )}
                        {isValid === false && (
                          <Badge
                            variant="destructive"
                            className="mt-1 shadow-none"
                          >
                            <XCircle className="w-3 h-3 mr-1" /> Tidak Valid
                          </Badge>
                        )}
                        {isValid === null && (
                          <Badge
                            variant="secondary"
                            className="mt-1 shadow-none bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                          >
                            Belum Diperiksa
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Body Kanan (Scrollable Data) */}
                  <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
                    <div className="space-y-1">
                      <DataRow label="Nama Lengkap" value={item.nama} />
                      <DataRow
                        label="NIK"
                        value={
                          <span className="font-mono text-[13px]">
                            {item.nik}
                          </span>
                        }
                      />
                      <DataRow
                        label="Status Hidup"
                        value={
                          item.status === "H"
                            ? "Hidup"
                            : item.status === "C"
                              ? "Bercerai"
                              : item.status === "M"
                                ? "Meninggal"
                                : "-"
                        }
                      />
                      <DataRow
                        label="Tanggal Lahir"
                        value={
                          item.tanggal_lahir
                            ? formatDateIndonesia(item.tanggal_lahir, "medium")
                            : "-"
                        }
                      />
                      <DataRow
                        label="Pendidikan"
                        value={item.jenjang_pendidikan?.nama_jenjang_pendidikan}
                      />
                      <DataRow label="Alamat" value={item.jalan} />
                      <DataRow
                        label="Pekerjaan"
                        value={item.pekerjaan?.pekerjaan}
                        highlight
                      />

                      {item.pekerjaan?.pekerjaan.startsWith("PNS") && (
                        <DataRow
                          label="Pangkat / Golongan"
                          value={`${item.golongan?.pangkat || ""} ${item.golongan?.golongan || ""}`}
                        />
                      )}

                      <DataRow
                        label="Rentang Penghasilan"
                        value={item.ref_penghasilan?.penghasilan_text}
                      />
                      <DataRow
                        label="Nominal Kotor / Bulan"
                        value={
                          item.penghasilan
                            ? formatRupiah(Number(item.penghasilan))
                            : "-"
                        }
                        highlight
                      />
                      {item.ref_desil?.desil && (
                        <DataRow
                          label="Desil"
                          value={item.ref_desil.desil}
                          highlight
                        />
                      )}

                      {item.catatan_verifikasi && (
                        <div className="mt-4 p-4 bg-red-50 dark:bg-red-950/30 rounded-xl border border-red-100 dark:border-red-900/50">
                          <p className="text-xs font-semibold text-red-800 dark:text-red-400 mb-1">
                            Catatan Koreksi Verifikator:
                          </p>
                          <p className="text-sm text-red-700 dark:text-red-300 italic">
                            "{item.catatan_verifikasi}"
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer Kanan (Action Buttons) */}
                  <div className="p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3">
                    {isValid !== null ? (
                      <Button
                        variant="outline"
                        onClick={() => handleReset(item.id_data_keluarga)}
                        disabled={isPendingItemAction}
                        className="w-full sm:w-auto dark:border-slate-700"
                      >
                        {isPendingResetValidation ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <GiBackwardTime className="mr-2 h-4 w-4" />
                        )}
                        Reset Hasil
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="destructive"
                          disabled={isPendingItemAction}
                          className="w-full sm:w-auto"
                          onClick={() => {
                            setShowInput(true);
                            setSelectedIdDataKeluarga(item.id_data_keluarga);
                            setForm((prev) => ({
                              ...prev,
                              nama_hubungan_keluarga:
                                item.hubungan_keluarga?.hubungan_keluarga,
                              id_ref_pekerjaan: item.id_ref_pekerjaan || "",
                              id_ref_penghasilan: item.id_ref_penghasilan || "",
                              penghasilan: Number(item.penghasilan) || 0,
                              id_ref_desil: "",
                              nik: item.nik,
                              isDesilRequired,
                            }));
                          }}
                        >
                          <XCircle className="w-4 h-4 mr-2" /> Tidak Valid
                        </Button>
                        <Button
                          className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white"
                          disabled={isPendingItemAction}
                          onClick={() => {
                            setShowInputValid(true);
                            setSelectedIdDataKeluarga(item.id_data_keluarga);
                            setForm((prev) => ({
                              ...prev,
                              nama_hubungan_keluarga:
                                item.hubungan_keluarga?.hubungan_keluarga,
                              id_ref_desil: "",
                              nik: item.nik,
                              isDesilRequired,
                            }));
                          }}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Valid (Sesuai)
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })
      ) : (
        <div className="p-10 text-center bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
          <Users className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <p className="text-slate-500 font-medium">
            Data Keluarga Tidak Ditemukan
          </p>
        </div>
      )}

      {/* --- KARTU KELUARGA & TANGGUNGAN --- */}
      <Card className="overflow-hidden border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl mt-4">
        <div className="grid xl:grid-cols-2 grid-cols-1 divide-y xl:divide-y-0 xl:divide-x divide-slate-100 dark:divide-slate-800">
          <div className="flex flex-col bg-slate-50/50 dark:bg-slate-900/20">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
              <div className="p-2 bg-primary/10 text-primary rounded-lg">
                <Users size={20} />
              </div>
              <div>
                <h2 className="font-bold text-slate-800 dark:text-slate-200 text-base">
                  Kartu Keluarga
                </h2>
                <p className="text-xs text-slate-500">
                  Cocokkan anggota keluarga dengan KK
                </p>
              </div>
            </div>
            <div className="relative h-[60vh] xl:h-[70vh] w-full bg-slate-200/50 dark:bg-slate-950 p-2">
              <iframe
                src={berkasKartuKeluarga?.path_berkas || ""}
                title="Kartu Keluarga"
                className="absolute inset-0 h-full w-full rounded-b-xl xl:rounded-bl-xl xl:rounded-br-none bg-white shadow-inner"
              />
            </div>
          </div>

          <div className="flex flex-col bg-white dark:bg-slate-900">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/20">
              <div>
                <h2 className="font-bold text-lg text-slate-800 dark:text-slate-100">
                  Data Tanggungan
                </h2>
                {dataPendaftaran?.is_valid_tanggungan === true && (
                  <Badge variant="success" className="mt-2 shadow-none">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Valid
                  </Badge>
                )}
                {dataPendaftaran?.is_valid_tanggungan === false && (
                  <Badge variant="destructive" className="mt-2 shadow-none">
                    <XCircle className="w-3 h-3 mr-1" /> Tidak Valid
                  </Badge>
                )}
                {dataPendaftaran?.is_valid_tanggungan === null && (
                  <Badge
                    variant="secondary"
                    className="mt-2 shadow-none bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                  >
                    Belum Diperiksa
                  </Badge>
                )}
              </div>
            </div>

            <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
              <div className="space-y-1">
                <DataRow label="Nama Siswa" value={dataSiswa?.nama_lengkap} />
                <DataRow
                  label="Jumlah Tanggungan Ortu / Wali"
                  value={
                    dataPendaftaran?.ref_tanggungan_asli?.jumlah_tanggungan_text
                  }
                  highlight
                />
                <DataRow
                  label="Biaya Hidup Perbulan"
                  value={
                    dataPendaftaran?.biaya_hidup_keluarga_perbulan
                      ? formatRupiah(
                          Number(
                            dataPendaftaran?.biaya_hidup_keluarga_perbulan,
                          ),
                        )
                      : "-"
                  }
                  highlight
                />
                <DataRow
                  label="Ada Anggota Keluarga Lulusan Sarjana?"
                  value={
                    String(dataPendaftaran?.is_ada_sarjana_keluarga) == "1"
                      ? "Ada"
                      : String(dataPendaftaran?.is_ada_sarjana_keluarga) == "0"
                        ? "Tidak Ada"
                        : "-"
                  }
                  highlight
                />
                {String(dataPendaftaran?.is_ada_sarjana_keluarga) == "1" && (
                  <DataRow
                    label="Nama Anggota Keluarga (Sarjana)"
                    value={dataPendaftaran?.nama_sarjana_keluarga || "-"}
                  />
                )}

                {dataPendaftaran?.data_survey
                  ?.ket_biaya_hidup_lebih_penghasilan && (
                  <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-100 dark:border-amber-900/50">
                    <p className="text-xs font-semibold text-amber-800 dark:text-amber-500 mb-1">
                      Kenapa biaya hidup melebihi penghasilan:
                    </p>
                    <p className="text-sm text-amber-900 dark:text-amber-400 italic">
                      "
                      {
                        dataPendaftaran.data_survey
                          .ket_biaya_hidup_lebih_penghasilan
                      }
                      "
                    </p>
                  </div>
                )}

                {dataPendaftaran?.catatan_verifikasi_tanggungan && (
                  <div className="mt-4 p-4 bg-red-50 dark:bg-red-950/30 rounded-xl border border-red-100 dark:border-red-900/50">
                    <p className="text-xs font-semibold text-red-800 dark:text-red-400 mb-1">
                      Catatan Koreksi Verifikator:
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300 italic">
                      "{dataPendaftaran.catatan_verifikasi_tanggungan}"
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3">
              {dataPendaftaran?.is_valid_tanggungan !== null ? (
                <Button
                  variant="outline"
                  onClick={() =>
                    handleResetValidationJumlahTanggungan(
                      String(dataPendaftaran?.id_data_pendaftaran),
                    )
                  }
                  disabled={isPendingResetValidationJumlahTanggungan}
                  className="w-full sm:w-auto dark:border-slate-700"
                >
                  {isPendingResetValidationJumlahTanggungan ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <GiBackwardTime className="mr-2 h-4 w-4" />
                  )}
                  Reset Hasil
                </Button>
              ) : (
                <>
                  <Button
                    variant="destructive"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      setShowInputJumlahTanggungan(true);
                      setFormTanggungan((prev) => ({
                        ...prev,
                        jumlah_tanggungan_orangtua_wali:
                          dataPendaftaran?.jumlah_tanggungan_orangtua_wali ||
                          "",
                        biaya_hidup_keluarga_perbulan:
                          dataPendaftaran?.biaya_hidup_keluarga_perbulan,
                        is_ada_sarjana_keluarga:
                          String(dataPendaftaran?.is_ada_sarjana_keluarga) ==
                          "1"
                            ? "1"
                            : String(
                                  dataPendaftaran?.is_ada_sarjana_keluarga,
                                ) == "0"
                              ? "0"
                              : "",
                        nama_sarjana_keluarga:
                          dataPendaftaran?.nama_sarjana_keluarga || "",
                      }));
                    }}
                    disabled={isPendingValidateJumlahTanggungan}
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Tidak Valid
                  </Button>
                  <Button
                    className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white"
                    disabled={isPendingValidateJumlahTanggungan}
                    onClick={() =>
                      handleValidateJumlahTanggungan(
                        String(dataPendaftaran.id_data_pendaftaran),
                        true,
                      )
                    }
                  >
                    {isPendingValidateJumlahTanggungan ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Valid (Sesuai)
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* --- BERKAS PANTI ASUHAN --- */}
      {dataSiswa?.path_bukti_panti_asuhan && (
        <Card className="overflow-hidden border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl mt-4">
          <div className="grid xl:grid-cols-2 grid-cols-1 divide-y xl:divide-y-0 xl:divide-x divide-slate-100 dark:divide-slate-800">
            <div className="flex flex-col bg-slate-50/50 dark:bg-slate-900/20">
              <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
                <div className="p-2 bg-primary/10 text-primary rounded-lg">
                  <FileText size={20} />
                </div>
                <div>
                  <h2 className="font-bold text-slate-800 dark:text-slate-200 text-base">
                    Berkas Panti Asuhan
                  </h2>
                  <p className="text-xs text-slate-500">
                    Surat Keterangan Panti
                  </p>
                </div>
              </div>
              <div className="relative h-[60vh] xl:h-[70vh] w-full bg-slate-200/50 dark:bg-slate-950 p-2">
                <iframe
                  src={dataSiswa?.path_bukti_panti_asuhan}
                  className="absolute inset-0 h-full w-full rounded-b-xl xl:rounded-bl-xl xl:rounded-br-none bg-white shadow-inner"
                />
              </div>
            </div>

            <div className="flex flex-col bg-white dark:bg-slate-900">
              <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/20">
                <div>
                  <h2 className="font-bold text-lg text-slate-800 dark:text-slate-100">
                    Data Panti Asuhan
                  </h2>
                  {dataSiswa?.status_valid_panti_asuhan === true && (
                    <Badge variant="success" className="mt-2 shadow-none">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Valid
                    </Badge>
                  )}
                  {dataSiswa?.status_valid_panti_asuhan === false && (
                    <Badge variant="destructive" className="mt-2 shadow-none">
                      <XCircle className="w-3 h-3 mr-1" /> Tidak Valid
                    </Badge>
                  )}
                  {dataSiswa?.status_valid_panti_asuhan === null && (
                    <Badge
                      variant="secondary"
                      className="mt-2 shadow-none bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                    >
                      Belum Diperiksa
                    </Badge>
                  )}
                </div>
              </div>

              <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
                <div className="space-y-1">
                  <DataRow label="Nama Siswa" value={dataSiswa?.nama_lengkap} />
                  <DataRow
                    label="Nama Panti Asuhan"
                    value={dataSiswa?.nama_panti_asuhan}
                    highlight
                  />

                  {dataSiswa?.catatan_valid_panti_asuhan && (
                    <div className="mt-4 p-4 bg-red-50 dark:bg-red-950/30 rounded-xl border border-red-100 dark:border-red-900/50">
                      <p className="text-xs font-semibold text-red-800 dark:text-red-400 mb-1">
                        Alasan Penolakan:
                      </p>
                      <p className="text-sm text-red-700 dark:text-red-300 italic">
                        "{dataSiswa.catatan_valid_panti_asuhan}"
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3">
                {dataSiswa?.status_valid_panti_asuhan !== null ? (
                  <Button
                    variant="outline"
                    onClick={() =>
                      handleResetValidationPantiAsuhan(
                        String(dataPendaftaran?.id_data_pendaftaran),
                      )
                    }
                    disabled={isPendingResetValidationPantiAsuhan}
                    className="w-full sm:w-auto dark:border-slate-700"
                  >
                    {isPendingResetValidationPantiAsuhan ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <GiBackwardTime className="mr-2 h-4 w-4" />
                    )}
                    Reset Hasil
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="destructive"
                      className="w-full sm:w-auto"
                      onClick={() => setShowInputPantiAsuhan(true)}
                      disabled={isPendingValidatePantiAsuhan}
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Tidak Valid
                    </Button>
                    <Button
                      className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white"
                      disabled={isPendingValidatePantiAsuhan}
                      onClick={() => {
                        if (dataPendaftaran)
                          handleValidatePantiAsuhan(
                            String(dataPendaftaran.id_data_pendaftaran),
                            true,
                          );
                      }}
                    >
                      {isPendingValidatePantiAsuhan ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      )}
                      Valid (Sesuai)
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ================= MODALS ================= */}

      {/* Modal Validasi Sesuai (Valid) Data Keluarga */}
      <Modal show={showInputValid} onClose={() => setShowInputValid(false)}>
        <div className="p-1 space-y-5">
          <FormHeader
            title={`Validasi Sesuai Data ${form.nama_hubungan_keluarga || ""}`}
            description="Tentukan Desil untuk data keluarga ini."
            icon={<CheckCircle2 className="text-emerald-500 h-6 w-6" />}
          />
          <div className="bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-slate-100 dark:border-slate-800">
            <div className="flex justify-between items-start sm:items-center flex-col sm:flex-row gap-2 sm:gap-0">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                  NIK Anggota Keluarga
                </p>
                <p className="font-mono font-bold text-slate-800 dark:text-slate-100">
                  {form.nik || "-"}
                </p>
              </div>
              {berkasKartuKeluarga?.path_berkas && (
                <a
                  href={berkasKartuKeluarga.path_berkas}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-md border border-blue-100 dark:border-blue-800/50"
                >
                  <FileText className="w-4 h-4" />
                  Lihat Kartu Keluarga
                </a>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900/50">
              <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-500" />
              <AlertDescription className="text-blue-700/80 dark:text-blue-500/80 ml-2 text-sm">
                Informasi detail terkait desil dapat dicek melalui portal resmi{" "}
                <a
                  href="https://cekbansos.kemensos.go.id/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold underline hover:text-blue-800 dark:hover:text-blue-400 transition-colors"
                >
                  Cek Bansos Kemensos
                </a>
                .
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">
                Pilih Desil{" "}
                {form.isDesilRequired ? (
                  <span className="text-red-500">*</span>
                ) : (
                  <span className="text-slate-500 font-normal italic ml-1">
                    (Opsional)
                  </span>
                )}
              </Label>
              <Combobox
                open={openDesil}
                setOpen={setOpenDesil}
                value={form.id_ref_desil || ""}
                setValue={(v) => setForm((p) => ({ ...p, id_ref_desil: v }))}
                options={dataDesil}
                placeholder="Pilih desil..."
              />
              <InputError
                text={error?.response?.data?.errors?.id_ref_desil?.join(",")}
              />
              {form.nama_hubungan_keluarga?.toLowerCase() === "ayah" &&
                !form.isDesilRequired && (
                  <p className="text-xs text-amber-600 mt-1">
                    Keterangan: Inputan desil untuk Ayah bersifat opsional
                    karena status Ayah telah meninggal.
                  </p>
                )}
              {form.nama_hubungan_keluarga?.toLowerCase() === "ibu" && (
                <p className="text-xs text-amber-600 mt-1">
                  Keterangan: Inputan desil untuk Ibu bersifat{" "}
                  {form.isDesilRequired
                    ? "wajib karena status Ayah meninggal"
                    : "opsional"}
                  .
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowInputValid(false)}
              disabled={isPendingValidate}
              className="dark:border-slate-700"
            >
              Batal
            </Button>
            <Button
              type="button"
              onClick={() => handleValidate(selectedIdDataKeluarga, true)}
              disabled={isPendingValidate}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isPendingValidate ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                "Simpan Validasi"
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Koreksi Data Keluarga */}
      <Modal show={showInput} onClose={() => setShowInput(false)}>
        <div className="p-1 space-y-5">
          <FormHeader
            title={`Koreksi Data ${form.nama_hubungan_keluarga || ""}`}
            description="Inputkan nilai yang benar sesuai dengan dokumen fisik."
            icon={<MdModeEdit className="text-primary h-6 w-6" />}
          />
          <div className="bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-slate-100 dark:border-slate-800">
            <div className="flex justify-between items-start sm:items-center flex-col sm:flex-row gap-2 sm:gap-0">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                  NIK Anggota Keluarga
                </p>
                <p className="font-mono font-bold text-slate-800 dark:text-slate-100">
                  {form.nik || "-"}
                </p>
              </div>
              {berkasKartuKeluarga?.path_berkas && (
                <a
                  href={berkasKartuKeluarga.path_berkas}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-md border border-blue-100 dark:border-blue-800/50"
                >
                  <FileText className="w-4 h-4" />
                  Lihat Kartu Keluarga
                </a>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900/50">
              <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-500" />
              <AlertDescription className="text-blue-700/80 dark:text-blue-500/80 ml-2 text-sm">
                Informasi detail terkait desil dapat dicek melalui portal resmi{" "}
                <a
                  href="https://cekbansos.kemensos.go.id/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold underline hover:text-blue-800 dark:hover:text-blue-400 transition-colors"
                >
                  Cek Bansos Kemensos
                </a>
                .
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">
                Pilih Desil{" "}
                {form.isDesilRequired ? (
                  <span className="text-red-500">*</span>
                ) : (
                  <span className="text-slate-500 font-normal italic ml-1">
                    (Opsional)
                  </span>
                )}
              </Label>
              <Combobox
                open={openDesil}
                setOpen={setOpenDesil}
                value={form.id_ref_desil || ""}
                setValue={(v) => setForm((p) => ({ ...p, id_ref_desil: v }))}
                options={dataDesil}
                placeholder="Pilih desil..."
              />
              <InputError
                text={error?.response?.data?.errors?.id_ref_desil?.join(",")}
              />
              {form.nama_hubungan_keluarga?.toLowerCase() === "ayah" &&
                !form.isDesilRequired && (
                  <p className="text-xs text-amber-600 mt-1">
                    Keterangan: Inputan desil untuk Ayah bersifat opsional
                    karena status Ayah telah meninggal.
                  </p>
                )}
              {form.nama_hubungan_keluarga?.toLowerCase() === "ibu" && (
                <p className="text-xs text-amber-600 mt-1">
                  Keterangan: Inputan desil untuk Ibu bersifat{" "}
                  {form.isDesilRequired
                    ? "wajib karena status Ayah meninggal"
                    : "opsional"}
                  .
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">
                Pekerjaan Sebenarnya
              </Label>
              <Combobox
                open={openPekerjaan}
                setOpen={setOpenPekerjaan}
                value={form.id_ref_pekerjaan || ""}
                setValue={(v) =>
                  setForm((p) => ({ ...p, id_ref_pekerjaan: v }))
                }
                options={dataPekerjaan}
                placeholder="Pilih pekerjaan"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">
                Penghasilan Kotor / bulan
              </Label>
              <Input
                placeholder="Misal: 3500000"
                value={form.penghasilan ? formatRupiah(form.penghasilan) : ""}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    penghasilan: Number(e.target.value.replace(/\D/g, "")),
                  }))
                }
                className="font-mono bg-slate-50 dark:bg-slate-950 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">
                Catatan Verifikator <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="Jelaskan kesalahan data..."
                value={form.catatan_verifikasi}
                onChange={(e) =>
                  setForm((p) => ({ ...p, catatan_verifikasi: e.target.value }))
                }
                rows={3}
                className="bg-slate-50 dark:bg-slate-950 focus-visible:ring-primary"
              />
              <InputError
                text={error?.response?.data?.errors?.catatan_verifikasi?.join(
                  ",",
                )}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowInput(false)}
              disabled={isPendingValidate}
              className="dark:border-slate-700"
            >
              Batal
            </Button>
            <Button
              type="button"
              onClick={() => handleValidate(selectedIdDataKeluarga, false)}
              disabled={
                isPendingValidate ||
                (form.isDesilRequired ? !form.id_ref_desil : false)
              }
              variant="destructive"
            >
              {isPendingValidate ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                "Simpan Koreksi"
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Koreksi Tanggungan */}
      <Modal
        show={showInputJumlahTanggungan}
        onClose={() => setShowInputJumlahTanggungan(false)}
      >
        <div className="p-1 space-y-5">
          <FormHeader
            title="Koreksi Tanggungan"
            description="Sesuaikan dengan Kartu Keluarga."
            icon={<MdModeEdit className="text-primary h-6 w-6" />}
          />

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">
                Jumlah Tanggungan Sebenarnya
              </Label>
              <Combobox
                open={openJumlahTanggungan}
                setOpen={setOpenJumlahTanggungan}
                value={formTanggungan.jumlah_tanggungan_orangtua_wali}
                setValue={(v) =>
                  setFormTanggungan((p) => ({
                    ...p,
                    jumlah_tanggungan_orangtua_wali: v,
                  }))
                }
                options={dataJumlahTanggungan}
                placeholder="Pilih jumlah tanggungan"
              />
            </div>
            {isVerifikatorUktAll && (
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">
                  Biaya Hidup Keluarga / bulan
                </Label>
                <Input
                  placeholder="Input biaya hidup sesungguhnya"
                  value={
                    formTanggungan.biaya_hidup_keluarga_perbulan
                      ? formatRupiah(
                          Number(formTanggungan.biaya_hidup_keluarga_perbulan),
                        )
                      : ""
                  }
                  onChange={(e) =>
                    setFormTanggungan((p) => ({
                      ...p,
                      biaya_hidup_keluarga_perbulan: e.target.value.replace(
                        /\D/g,
                        "",
                      ),
                    }))
                  }
                  className="font-mono bg-slate-50 dark:bg-slate-950 focus-visible:ring-primary"
                />
                <InputError
                  text={errorValidateJumlahTanggungan?.response?.data?.errors?.biaya_hidup_keluarga_perbulan?.join(
                    ",",
                  )}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>
                <Badge variant={"outline"}>Isian Peserta: </Badge> Apakah dalam
                Kartu Keluarga Anda terdapat anggota keluarga yang telah lulus
                pendidikan Sarjana?
              </Label>
              <Combobox
                open={openSarjana}
                setOpen={setOpenSarjana}
                value={String(formTanggungan.is_ada_sarjana_keluarga || "")}
                setValue={(v) => {
                  if (!v) return;
                  setFormTanggungan((prev) => ({
                    ...prev,
                    is_ada_sarjana_keluarga: v,
                    ...(v === "0" ? { nama_sarjana_keluarga: "" } : {}),
                  }));
                }}
                options={[
                  { label: "Ada", value: "1" },
                  { label: "Tidak Ada", value: "0" },
                ]}
                placeholder="Pilih Ada/Tidak Ada"
              />
              <InputError
                text={errorValidateJumlahTanggungan?.response?.data?.errors?.is_ada_sarjana_keluarga?.join(
                  ",",
                )}
              />
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-900/50">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-500">
                  <AlertCircle className="inline w-4 h-4 mr-1.5 mb-0.5" />
                  Catatan: Jika isian peserta menyatakan "Ada" namun
                  pendidikannya belum diperbarui di Kartu Keluarga atau gelarnya
                  tidak ada, maka biarkan saja (jangan diubah).
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">
                Catatan Verifikator <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="Jelaskan kenapa tidak valid..."
                value={formTanggungan.catatan_verifikasi_tanggungan}
                onChange={(e) =>
                  setFormTanggungan((p) => ({
                    ...p,
                    catatan_verifikasi_tanggungan: e.target.value,
                  }))
                }
                rows={3}
                className="bg-slate-50 dark:bg-slate-950 focus-visible:ring-primary"
              />
              <InputError
                text={errorValidateJumlahTanggungan?.response?.data?.errors?.catatan_verifikasi_tanggungan?.join(
                  ",",
                )}
              />
            </div>

            {/* {formTanggungan.is_ada_sarjana_keluarga === "1" && (
              <div className="space-y-2">
                <Label required>Nama Anggota Keluarga (Sarjana)</Label>
                <Input
                  value={formTanggungan.nama_sarjana_keluarga || ""}
                  onChange={(e) =>
                    setFormTanggungan((p) => ({
                      ...p,
                      nama_sarjana_keluarga: e.target.value,
                    }))
                  }
                  placeholder="Masukkan nama sarjana..."
                  className="bg-slate-50 dark:bg-slate-950 focus-visible:ring-primary"
                />
                <InputError
                  text={errorValidateJumlahTanggungan?.response?.data?.errors?.nama_sarjana_keluarga?.join(
                    ",",
                  )}
                />
              </div>
            )} */}
          </div>

          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowInputJumlahTanggungan(false)}
              disabled={isPendingValidateJumlahTanggungan}
              className="dark:border-slate-700"
            >
              Batal
            </Button>
            <Button
              type="button"
              onClick={() =>
                handleValidateJumlahTanggungan(
                  String(dataPendaftaran?.id_data_pendaftaran),
                  false,
                )
              }
              disabled={isPendingValidateJumlahTanggungan}
              variant="destructive"
            >
              {isPendingValidateJumlahTanggungan ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                "Simpan Koreksi"
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Koreksi Panti Asuhan */}
      <Modal
        show={showInputPantiAsuhan}
        onClose={() => setShowInputPantiAsuhan(false)}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleValidatePantiAsuhan(
              String(dataPendaftaran?.id_data_pendaftaran),
              false,
            );
          }}
          className="p-1 space-y-5"
        >
          <FormHeader
            title="Tolak Berkas Panti Asuhan"
            description="Berikan alasan penolakan dokumen ini."
            icon={<XCircle className="text-red-500 h-6 w-6" />}
          />
          <div className="space-y-2">
            <Label className="text-slate-700 dark:text-slate-300">
              Alasan Penolakan <span className="text-red-500">*</span>
            </Label>
            <Textarea
              required
              placeholder="Misal: Surat keterangan buram / kedaluwarsa..."
              value={formPantiAsuhan.catatan_verifikator}
              onChange={(e) =>
                setFormPantiAsuhan((p) => ({
                  ...p,
                  catatan_verifikator: e.target.value,
                }))
              }
              rows={4}
              className="bg-slate-50 dark:bg-slate-950 focus-visible:ring-red-500"
            />
            <InputError
              text={errorValidatePantiAsuhan?.response?.data?.errors?.catatan_verifikator?.join(
                ",",
              )}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowInputPantiAsuhan(false)}
              disabled={isPendingValidatePantiAsuhan}
              className="dark:border-slate-700"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={isPendingValidatePantiAsuhan}
              variant="destructive"
            >
              {isPendingValidatePantiAsuhan ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                "Simpan Penolakan"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
